/**
 * Client entrypoint: boots the Phaser game into #game-root.
 *
 * Cross-platform (D8, HARD requirement): the Scale Manager RESIZE mode tracks the
 * viewport so the game fills any screen on iPhone Chrome/Safari + desktop browsers;
 * the canvas is touch-friendly (see index.html). The Layout helper (scenes/layout)
 * reads safe-area insets + orientation so every scene adapts phone <-> desktop.
 *
 * Scene flow (all scenes registered here, navigated by SceneFlow.goTo via string key):
 *   Boot -> Preload -> Title -> { Continue | NewGame } -> Town
 *   Town -> RegionMap -> Route -> Battle -> back (Route)
 *   Town also opens: TeamBoxes, Dex, Settings, Codex, Trade (and Guild/Chat stubs).
 *
 * The renderer owns no game rules — every scene reads truth from the live GameSession.
 */
import Phaser from 'phaser';
import {
  BootScene,
  PreloadScene,
  AuthGateScene,
  TitleScene,
  NewGameScene,
  TownScene,
  RegionMapScene,
  RouteScene,
  BattleScene,
  TrainingScene,
  TeamBoxesScene,
  DexScene,
  SettingsScene,
  LoreCodexScene,
  TradeScene,
  STUB_SCENES,
  REGISTRY_SESSION,
} from './scenes/index.js';
// Quest/story layer (owner decision D17): the Quest Log + the gym-battle wrapper.
// Imported directly from their files (not re-exported via scenes/index) so this single
// main.ts edit owns both registrations.
import { QuestLogScene } from './scenes/QuestLogScene.js';
import { GymScene } from './scenes/GymScene.js';
// Round 3 arcs (registered here directly). LeagueScene is this agent's; CareScene is a
// sibling agent's, written in parallel — its class/key are fixed ('Care'), so the
// registration lands now even if the file arrives moments later.
import { LeagueScene } from './scenes/LeagueScene.js';
import { CareScene } from './scenes/CareScene.js';
import type { GameSession } from './session/index.js';
import { InactivityMonitor } from './session/index.js';
import { clearProxyToken } from './store/index.js';

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game-root',
  backgroundColor: '#1a2233',
  scale: {
    mode: Phaser.Scale.RESIZE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: '100%',
    height: '100%',
  },
  render: {
    // Crisp PMD chibi + game-ripped sprites (no blurry upscaling on retina).
    pixelArt: true,
  },
  input: {
    activePointers: 3, // multi-touch friendly
  },
  // Boot is the active entry scene (first in the list); the rest are registered
  // inactive and reached by key via SceneFlow.goTo. Contract keys (from the scene
  // classes' super(key)): 'Boot','Preload','Title','NewGame','Town','RegionMap',
  // 'Route','Battle','Training','TeamBoxes','Dex','Settings','Codex','Trade','Quests',
  // 'Gym', + stubs Chat/Guild.
  scene: [
    BootScene,
    PreloadScene,
    AuthGateScene,
    TitleScene,
    NewGameScene,
    TownScene,
    RegionMapScene,
    RouteScene,
    BattleScene,
    TrainingScene,
    TeamBoxesScene,
    DexScene,
    SettingsScene,
    LoreCodexScene,
    TradeScene,
    QuestLogScene,
    GymScene,
    LeagueScene,
    CareScene,
    ...STUB_SCENES,
  ],
};

const game = new Phaser.Game(config);

// Expose the running game for debugging + headless/automated testing. Harmless in
// prod (a single reference); lets tooling drive scenes via window.prGame.scene.*.
(globalThis as unknown as { prGame?: Phaser.Game }).prGame = game;

// Last-resort save-on-close: the live session's own Autosave (started in setSession)
// already flushes on hide/close, but this game-level hook is a belt-and-braces net at
// the boundaries of a scene/session swap so closing the tab ALWAYS persists. Reads the
// live session off the shared game registry; non-blocking and never throws.
if (typeof window !== 'undefined') {
  const flushLiveSession = (): void => {
    const session = game.registry.get(REGISTRY_SESSION) as GameSession | undefined;
    if (session) void session.flush().catch(() => {});
  };
  window.addEventListener('pagehide', flushLiveSession);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushLiveSession();
  });

  // ── 15-minute inactivity auto-logoff ("if >15m inactivity, just log me off") ──────
  //
  // ONE app-wide monitor (started here so there's never a second). It watches real
  // user input at the window level (so it works regardless of the active Phaser scene)
  // and, after DEFAULT_INACTIVITY_TIMEOUT_MS of total silence, gracefully LOGS OFF:
  //   1) FLUSH the live session durably first (no progress lost — even mid-battle/
  //      dialog the snapshot is consistent; flush() never throws and leaves no pending
  //      timer);
  //   2) clearProxyToken() (an idle tab stops holding the session + stops syncing);
  //   3) return to the AuthGate so the next play must re-enter the password.
  // ANY activity resets the countdown, so an active player is never logged off. The
  // monitor fires once then stops itself; the flush is best-effort and guarded.
  const logoff = (): void => {
    // 1) Final durable save. flush() is async + guarded; fire-and-forget so a slow
    //    network can't block the transition, but it WILL complete the write/put.
    const session = game.registry.get(REGISTRY_SESSION) as GameSession | undefined;
    if (session) void session.flush().catch(() => {});

    // 2) Drop the bearer token — logged off, stops syncing + holding the session.
    try {
      clearProxyToken();
    } catch {
      // clearProxyToken is itself guarded; belt-and-braces.
    }

    // 3) Back to the AuthGate. Stop every running scene (incl. overlays like Chat) and
    //    start AuthGate fresh, so its probe re-runs and — with the token now gone —
    //    prompts for the password again (or auto-skips when none is configured/offline).
    //    Falls back to a full reload if the scene manager isn't reachable, so an idle
    //    tab always ends up logged off rather than stranded.
    try {
      const mgr = game.scene;
      for (const s of mgr.getScenes(true)) {
        const key = s.scene.key;
        if (key !== 'AuthGate') mgr.stop(key);
      }
      if (mgr.isActive('AuthGate')) {
        mgr.stop('AuthGate');
      }
      mgr.start('AuthGate', { fromInactivityLogoff: true });
    } catch {
      try {
        window.location.reload();
      } catch {
        // Nothing more we can do; the token is already cleared + the save flushed.
      }
    }
  };

  const inactivity = new InactivityMonitor(logoff);
  inactivity.start();
  // Expose for tuning / tests (e.g. window.prInactivity.remainingMs()). Harmless single ref.
  (globalThis as unknown as { prInactivity?: InactivityMonitor }).prInactivity = inactivity;
}
